/**
 * API utility for tracking and updating user usage (downloads, uploads, AI usage) in the database.
 */

const BASE_URL = "https://app.tifstudio.in/api";

/**
 * Update usage for the current user in the database.
 * @param {string} type - The type of usage to update ('download', 'upload', 'ai_usage').
 * @returns {Promise<object>} - The updated usage stats from the backend.
 */
const updateUsage = async (type) => {
    const userStr = localStorage.getItem("user");
    if (!userStr) return null;

    const user = JSON.parse(userStr);
    const email = user.email || user.username;

    if (!email) return null;

    try {
        const sessionToken = localStorage.getItem('session_token') || '';
        const response = await fetch(`${BASE_URL}/update-usage`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                email: email,
                usageType: type,
                sessionToken: sessionToken
            }),
        });

        const data = await response.json();
        if (data.success === false && data.message === 'SESSION_EXPIRED') {
            console.warn('Session expired. Logging out.');
            localStorage.removeItem('user');
            localStorage.removeItem('session_token');
            window.location.reload();
            return null;
        }

        if (data.success) {
            return data.usage; // Should contain current counts and limits
        }
    } catch (error) {
        console.error(`Error updating ${type} usage:`, error);
    }
    return null;
};

/**
 * Fetch current usage stats for the user from the database.
 * @returns {Promise<object>} - The usage stats.
 */
const fetchUsage = async () => {
    const userStr = localStorage.getItem("user");
    if (!userStr) return null;

    const user = JSON.parse(userStr);
    const email = user.email || user.username;

    if (!email) return null;

    try {
        const sessionToken = localStorage.getItem('session_token') || '';
        const response = await fetch(
            `${BASE_URL}/get-usage?email=${encodeURIComponent(email)}&sessionToken=${encodeURIComponent(sessionToken)}`,
        );
        const data = await response.json();
        
        if (data.success === false && data.message === 'SESSION_EXPIRED') {
            console.warn('Session expired. Logging out.');
            localStorage.removeItem('user');
            localStorage.removeItem('session_token');
            window.location.reload();
            return null;
        }

        if (data.success) {
            return data.usage;
        }
    } catch (error) {
        console.error("Error fetching usage:", error);
    }
    return null;
};

export { updateUsage, fetchUsage };
